<script lang="ts">
	import WhatsAppIcon from '$lib/components/icons/WhatsApp.svelte';
	import { Button } from '$lib/components/ui/button';
	import * as Card from '$lib/components/ui/card';
	import ArrowRight from '@lucide/svelte/icons/arrow-right';
	import ChevronRight from '@lucide/svelte/icons/chevron-right';
	import CircleCheck from '@lucide/svelte/icons/circle-check';
	import CircleQuestionMark from '@lucide/svelte/icons/circle-question-mark';
	import Mail from '@lucide/svelte/icons/mail';
	import MapPin from '@lucide/svelte/icons/map-pin';
	import MessageCircle from '@lucide/svelte/icons/message-circle';
	import Phone from '@lucide/svelte/icons/phone';
	import Shield from '@lucide/svelte/icons/shield';
	import Users from '@lucide/svelte/icons/users';

	const whatsappContactHref = `https://wa.me/34679855762?text=${encodeURIComponent(
		'Hola, tengo dudas sobre el alquiler de lista septima'
	)}`;

	const contactInfo = [
		{
			icon: Mail,
			title: 'Email',
			value: 'info@zarpar.eu',
			description: 'Envíanos un email y te responderemos en menos de 24 horas',
			action: 'mailto:info@zarpar.eu',
			actionText: 'Enviar Email'
		},
		{
			icon: Phone,
			title: 'Teléfono',
			value: '+34 679 855 762',
			description: 'Llámanos de lunes a viernes de 9:00 a 20:00',
			action: 'tel:+34679855762',
			actionText: 'Llamar Ahora'
		},
		{
			icon: MapPin,
			title: 'En Persona',
			value: 'Provincia de Pontevedra',
			description: 'Llamanos, quedamos, te invitamos a un cafe y te explicamos Zarpar',
			action: '',
			actionText: 'Llamar Ahora'
		}
	];

	const services = [
		{
			icon: Shield,
			title: 'Soporte Técnico',
			description: 'Ayuda con la plataforma y resolución de problemas técnicos'
		},
		{
			icon: Users,
			title: 'Atención al Cliente',
			description: 'Asesoramiento personalizado para propietarios y arrendatarios'
		},
		{
			icon: MessageCircle,
			title: 'Consultas Comerciales',
			description: 'Información sobre nuestros servicios y tarifas'
		},
		{
			icon: CircleCheck,
			title: 'Soporte Legal',
			description: 'Asesoramiento sobre normativas y documentación requerida'
		}
	];

	const faqs = [
		{
			question: '¿Cuál es el tiempo de respuesta típico?',
			answer:
				'Respondemos emails en menos de 24 horas y llamadas telefónicas de inmediato durante el horario laboral.'
		},
		{
			question: '¿Ofrecen soporte en otros idiomas?',
			answer:
				'Actualmente ofrecemos soporte en español e inglés. Estamos trabajando para añadir más idiomas.'
		},
		{
			question: '¿Puedo programar una cita en persona?',
			answer:
				'Sí, puedes programar una cita con nosotros en la provincia de Pontevedra. Contacta con nosotros para coordinar.'
		},
		{
			question: '¿Qué información necesito tener lista antes de contactar?',
			answer:
				'Para un servicio más eficiente, ten preparada información básica sobre tu barco y tus necesidades específicas.'
		},
		{
			question: '¿Ofrecen soporte fuera del horario laboral?',
			answer:
				'Para emergencias técnicas, contamos con un sistema de soporte 24/7. Consultas generales se atienden en horario laboral.'
		},
		{
			question: '¿Cómo puedo reportar un problema técnico?',
			answer:
				'Puedes reportar problemas técnicos por email a info@zarpar.eu o llamando al +34 679 855 762.'
		}
	];
</script>

<svelte:head>
	<title>Contacto - Zarpar</title>
	<meta
		name="description"
		content="Contacta con el equipo de Zarpar. Email: info@zarpar.eu | Teléfono: +34 679 855 762. Soporte técnico y atención al cliente disponible."
	/>
	<meta property="og:title" content="Contacto - Zarpar" />
	<meta
		property="og:description"
		content="Contacta con el equipo de Zarpar. Email: info@zarpar.eu | Teléfono: +34 679 855 762"
	/>
</svelte:head>

<!-- Hero Section -->
<section class="relative flex min-h-screen items-center justify-center overflow-hidden">
	<!-- Background Image -->
	<div class="absolute inset-0 z-0">
		<enhanced:img
			src="/static/img/landing/HomeBoatRentalHero.jpg"
			alt="Equipo de Zarpar"
			class="h-full w-full object-cover"
			fetchpriority="high"
			sizes="min(1280px, 100vw)"
		></enhanced:img>
		<div class="absolute inset-0 bg-black/60"></div>
	</div>

	<!-- Content -->
	<div class="relative z-10 container mx-auto px-4 text-center text-white">
		<div class="mx-auto -mt-12 max-w-4xl sm:mt-0">
			<!-- Badge -->
			<div
				class="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-600/30 bg-blue-600/20 px-4 py-2 text-blue-400"
			>
				<MessageCircle class="h-4 w-4" />
				<span class="text-sm font-medium">Atención al Cliente</span>
			</div>

			<h1 class="mb-6 text-4xl font-bold md:text-6xl">
				¿Necesitas
				<span class="text-blue-400">Ayuda?</span>
			</h1>

			<p class="mx-auto mb-8 max-w-3xl text-xl text-gray-200 md:text-2xl">
				Nuestro equipo está aquí para ayudarte con cualquier consulta sobre alquiler de barcos,
				<strong>Lista 7ª</strong> o el uso de nuestra plataforma
			</p>

			<div class="flex flex-col flex-wrap items-center justify-center gap-4 sm:flex-row">
				<Button
					href="mailto:info@zarpar.eu"
					size="lg"
					class="bg-blue-600 px-8 py-4 text-lg text-white hover:bg-blue-700"
				>
					<Mail class="mr-2 h-5 w-5" />
					Enviar Email
				</Button>
				<Button
					href="tel:+34679855762"
					variant="secondary"
					size="lg"
					class="border-white/20 bg-white/10 px-8 py-4 text-lg text-white hover:bg-white hover:text-blue-600"
				>
					<Phone class="mr-2 h-5 w-5" />
					Llamar Ahora
				</Button>
				<Button
					href={whatsappContactHref}
					target="_blank"
					rel="noopener noreferrer"
					size="lg"
					class="bg-[#25D366] px-8 py-4 text-lg text-white hover:bg-[#20BD5A]"
				>
					<WhatsAppIcon class="mr-2 shrink-0" size={20} />
					WhatsApp
				</Button>
			</div>
		</div>
	</div>

	<!-- Scroll indicator -->
	<div class="absolute bottom-8 left-1/2 -translate-x-1/2 transform animate-bounce">
		<ChevronRight class="h-6 w-6 rotate-90 text-white" />
	</div>
</section>

<!-- Contact Info Section -->
<section class="bg-gray-50 py-16">
	<div class="container mx-auto px-4">
		<div class="mb-12 text-center">
			<h2 class="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">Información de Contacto</h2>
			<p class="mx-auto max-w-2xl text-xl text-gray-600">
				Elige la forma más conveniente para contactar con nuestro equipo
			</p>
		</div>

		<div class="grid gap-6 md:grid-cols-3">
			{#each contactInfo as contact (contact.title)}
				<Card.Root class="text-center transition-shadow hover:shadow-lg">
					<Card.Content class="pt-6">
						<div
							class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100"
						>
							<svelte:component this={contact.icon} class="h-6 w-6 text-blue-600" />
						</div>
						<h3 class="mb-2 text-lg font-semibold text-gray-900">{contact.title}</h3>
						<p class="mb-2 text-xl font-bold text-blue-600">{contact.value}</p>
						<p class="mb-4 text-gray-600">{contact.description}</p>
						<Button href={contact.action} variant="outline" class="w-full">
							{contact.actionText}
							<ArrowRight class="ml-2 h-4 w-4" />
						</Button>
					</Card.Content>
				</Card.Root>
			{/each}
		</div>
	</div>
</section>

<!-- Services Section -->
<section class="bg-blue-50 py-16">
	<div class="container mx-auto px-4">
		<div class="mb-12 text-center">
			<h2 class="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">¿En qué podemos ayudarte?</h2>
			<p class="mx-auto max-w-2xl text-xl text-gray-600">
				Nuestro equipo especializado está preparado para resolver cualquier consulta
			</p>
		</div>

		<div class="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
			{#each services as service (service.title)}
				<Card.Root class="text-center transition-shadow hover:shadow-lg">
					<Card.Content class="pt-6">
						<div
							class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100"
						>
							<svelte:component this={service.icon} class="h-6 w-6 text-blue-600" />
						</div>
						<h3 class="mb-2 text-lg font-semibold text-gray-900">{service.title}</h3>
						<p class="text-gray-600">{service.description}</p>
					</Card.Content>
				</Card.Root>
			{/each}
		</div>
	</div>
</section>

<!-- FAQ Section -->
<section class="bg-gray-50 py-16">
	<div class="container mx-auto px-4">
		<div class="mb-12 text-center">
			<h2 class="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">Preguntas Frecuentes</h2>
			<p class="mx-auto max-w-2xl text-xl text-gray-600">
				Resolvemos las dudas más comunes sobre nuestro servicio
			</p>
		</div>

		<div class="mx-auto max-w-3xl space-y-6">
			{#each faqs as faq (faq.question)}
				<Card.Root class="transition-shadow hover:shadow-md">
					<Card.Content class="p-6">
						<div class="flex items-start gap-4">
							<CircleQuestionMark class="h-6 w-6 shrink-0 text-blue-600" />
							<div>
								<h3 class="mb-3 text-lg font-semibold text-gray-900">{faq.question}</h3>
								<p class="leading-relaxed text-gray-600">{faq.answer}</p>
							</div>
						</div>
					</Card.Content>
				</Card.Root>
			{/each}
		</div>
	</div>
</section>

<!-- CTA Section -->
<section class="bg-linear-to-r from-blue-600 to-blue-800 py-16 text-white">
	<div class="container mx-auto px-4 text-center">
		<div class="mx-auto max-w-3xl">
			<h2 class="mb-4 text-3xl font-bold md:text-4xl">¿Listo para empezar?</h2>
			<p class="mb-8 text-xl text-blue-100">
				Contacta con nosotros hoy mismo y descubre cómo podemos ayudarte con tu barco
			</p>

			<div class="flex flex-col justify-center gap-4 sm:flex-row">
				<Button
					href="mailto:info@zarpar.eu"
					size="lg"
					class="bg-white px-8 py-4 text-lg text-blue-600 hover:bg-gray-100"
				>
					<Mail class="mr-2 h-5 w-5" />
					Enviar Email
				</Button>
				<Button
					href="tel:+34679855762"
					variant="secondary"
					size="lg"
					class="border-white/20 bg-white/10 px-8 py-4 text-lg text-white hover:bg-white hover:text-blue-600"
				>
					<Phone class="mr-2 h-5 w-5" />
					Llamar Ahora
				</Button>
			</div>
		</div>
	</div>
</section>
